import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SendIcon from '@mui/icons-material/Send'
import { COUNTER_FROM, MAX_MESSAGE_CHARS } from '../../helpers/assistant'
import { formatCount } from '../../helpers/pagination'
import { BOT } from '../../theme/tokens'

/**
 * Where the person writes to BhanzuBot. Enter sends, Shift+Enter starts a
 * new line (and so does Enter while an input method is composing); at most
 * MAX_MESSAGE_CHARS, with the count shown near the limit. Send is off while
 * an answer is on its way, for an empty message, or when BhanzuBot is
 * switched off. A change of `focusToken` focuses it, caret at the end
 * (a prefill from the Guide).
 */
const Composer = ({ value, onChange, onSend, busy, disabled, focusToken }) => {
    const inputRef = useRef(null)
    const canSend = !busy && !disabled && value.trim().length > 0

    useEffect(() => {
        const input = inputRef.current
        if (!input || !focusToken) return
        input.focus()
        const end = input.value.length
        if (typeof input.setSelectionRange === 'function')
            input.setSelectionRange(end, end)
    }, [focusToken])

    const onKeyDown = (event) => {
        if (event.key !== 'Enter' || event.shiftKey) return
        if (event.nativeEvent?.isComposing) return
        event.preventDefault()
        if (canSend) onSend()
    }

    return (
        <Box
            sx={{
                p: 1.25,
                borderTop: `1px solid ${BOT.panelBorder}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <TextField
                    inputRef={inputRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={
                        disabled
                            ? 'BhanzuBot is switched off'
                            : 'Ask about Comms…'
                    }
                    disabled={Boolean(disabled)}
                    multiline
                    maxRows={5}
                    fullWidth
                    size="small"
                    inputProps={{
                        maxLength: MAX_MESSAGE_CHARS,
                        'aria-label': 'Message BhanzuBot',
                    }}
                />
                <IconButton
                    aria-label="Send"
                    onClick={onSend}
                    disabled={!canSend}
                    sx={{ color: BOT.launcherFg, mb: 0.25 }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
            {value.length >= COUNTER_FROM && (
                <Typography
                    aria-live="polite"
                    sx={{
                        typography: 'caption',
                        color: BOT.meta,
                        textAlign: 'right',
                    }}
                >
                    {formatCount(value.length)} /{' '}
                    {formatCount(MAX_MESSAGE_CHARS)}
                </Typography>
            )}
        </Box>
    )
}

Composer.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    disabled: PropTypes.string,
    focusToken: PropTypes.number,
}

export default Composer
